import type { ComponentType, FC } from 'react';
import React, { useEffect } from 'react';
import type { InferableComponentEnhancerWithProps } from 'react-redux';
import { connect } from 'react-redux';
import type { AnyAction, Dispatch } from 'redux';
import { formValueSelector, initialize } from 'redux-form';

import { Spinner } from 'app/components/commons/Spinner';
import { Card } from 'app/components/layout/Card';
import {
  tryFetchEntity,
  tryFetchEntityWithParams,
} from 'app/redux/actions/entities';
import type { ApplicationState } from 'app/redux/reducers';
import {
  entitiesSelector,
  isLoadingSelector,
  isReadySelector,
} from 'app/redux/selectors';

export const entityConnect = <Entities extends object, OwnProps>(
  entities: (keyof Entities)[],
  fields: string[] = []
): EntityConnect<Entities, OwnProps> => {
  const fieldsMap = Object.fromEntries(
    fields.map((field) => {
      const split = field.split('.');

      return [split[1], split];
    })
  );

  const mapStateToProps = (state: ApplicationState) => ({
    ready: entities.every((entity) => isReadySelector(entity)(state)),
    loading: entities.some((entity) => isLoadingSelector(entity)(state)),

    ...Object.fromEntries(
      entities.map((entity) => [entity, entitiesSelector(entity)(state)])
    ),

    ...Object.fromEntries(
      Object.entries(fieldsMap).map(([_, [formName, fieldName]]) => [
        fieldName,
        formValueSelector(formName)(state, fieldName),
      ])
    ),
  });

  const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
    fetchEntities: () =>
      entities.map((entity) => dispatch(tryFetchEntity(entity))),
    fetchEntitiesWithParams: (params: unknown) =>
      entities.map((entity) =>
        dispatch(tryFetchEntityWithParams(entity, params))
      ),
    initialize: (form: string, values: unknown) =>
      dispatch(
        initialize(form, values, {
          keepDirty: true,
          updateUnregisteredFields: true,
        })
      ),
    dispatch,
  });

  // @ts-ignore // TODO: fix typings later
  return connect(mapStateToProps, mapDispatchToProps);
};

type EntityConnectCommonProps = {
  ready: boolean;
  loading: boolean;
  fetchEntities: () => void;
  fetchEntitiesWithParams: (params: unknown) => void;
  initialize: (form: string, values: unknown) => void;
  dispatch: (action: AnyAction) => void;
};
type EntityConnect<Entities, OwnProps> = InferableComponentEnhancerWithProps<
  Entities & EntityConnectCommonProps,
  OwnProps
>;

export type EntityConnectProps<Entities, OwnProps> = Entities &
  OwnProps &
  EntityConnectCommonProps;

export const withEntities =
  <Entities extends object, OwnProps>(
    entities: (keyof Entities)[],
    fields: string[] = []
  ) =>
  (Component: ComponentType<EntityConnectProps<Entities, OwnProps>>) => {
    const EntityCardComponent: FC<EntityConnectProps<Entities, OwnProps>> = (
      props
    ) => {
      const { ready, loading, fetchEntities } = props;

      useEffect(() => {
        fetchEntities();
      }, [fetchEntities]);

      return (
        <Spinner spinning={loading}>
          {ready ? (
            // @ts-ignore -- legacy directive, needs investigation
            <Component {...props} />
          ) : (
            <Card title="Loading..." />
          )}
        </Spinner>
      );
    };

    // @ts-ignore // TODO: fix typing later
    return entityConnect(entities, fields)(EntityCardComponent);
  };
